<template>
	<Modal
		v-if="!profileUnavailable"
		:title="pageTitle"
		size="2xl"
		:frameless="true"
		panel-class="h-[calc(100vh-2rem)] px-2"
		@close="handleClose"
		@confirm="handleClose"
	>
		<div v-if="showNoCandidatesLeft" class="p-5 text-center">
			<IconUsers
				:size="48"
				class="block mx-auto mb-3 text-grey-500"
			/>
			<h3 class="mb-2 text-grey-500">
				{{ $t('general.no-candidates-left') }}
			</h3>
			<p class="text-grey-500">
				{{ $t('general.all-candidates-processed') }}
			</p>
			<Button type="primary" @click="handleClose">
				{{ $t('general.close') }}
			</Button>
		</div>

		<div v-else-if="error" class="p-5 text-center">
			<IconAlertTriangle
				:size="48"
				class="block mx-auto mb-3 text-red-500"
			/>
			<h3 class="mb-2 text-red-500">{{ $t('general.error-loading') }}</h3>
			<p class="mb-3 text-grey-500">{{ errorMessage }}</p>
			<div class="gap-2 flex justify-center">
				<Button type="primary" :loading="loading" @click="retryLoad">
					<IconRepeat :size="16" class="mr-1 shrink-0" />
					{{ $t('general.retry') }}
				</Button>
				<Button type="tertiary" @click="handleClose">
					{{ $t('general.close') }}
				</Button>
			</div>
		</div>

		<div v-else-if="applicantProfile" class="p-6">
			<div class="pb-3 mb-4 border-b border-gray-200">
				<div class="flex flex-wrap items-center justify-between gap-3">
					<h3
						class="m-0 font-heading font-black text-[20px] leading-[22px] tracking-[0.3px] uppercase text-neutral-900"
					>
						{{ jobOfferTitle }}
					</h3>
					<div
						v-if="applicantIds && applicantIds.length > 0"
						class="flex items-center gap-2 text-sm text-neutral-500"
					>
						<Button
							type="tertiary"
							size="sm"
							:aria-label="$t('general.previous')"
							:disabled="currentIndex <= 0"
							@click="navigatePrevious"
						>
							<IconArrowLeft :size="16" />
						</Button>
						<span>
							{{ currentIndex + 1 }} {{ $t('general.of') }}
							{{ applicantIds.length }}
						</span>
						<Button
							type="tertiary"
							size="sm"
							:aria-label="$t('general.next')"
							:disabled="currentIndex >= applicantIds.length - 1"
							@click="navigateNext"
						>
							<IconArrowRight :size="16" />
						</Button>
					</div>
				</div>
			</div>

			<ApplicantProfileHeader
				:applicant-profile-id="applicantProfile.id"
				:job-offer-id="jobOfferId"
				:applicant-avatar-style="applicantAvatarStyle"
				:applicant-full-name="applicantFullName"
				:applicant-email="isTalentPoolContext ? null : applicantEmail"
				:applicant-phone="isTalentPoolContext ? null : applicantPhone"
				:applicant-profile-link="
					isTalentPoolContext ? null : applicantProfileLink
				"
				:show-top-fit-badge="showTopFitBadge"
				:show-action-buttons="showHeaderActionButtons"
				:application="application"
				:job-offer="jobOffer"
				:is-org-context="$isOrgContext"
				:application-loading="applicationLoading"
				@invite="invite"
				@update="updateApplication"
				@contact="openApplicationInNewTab"
			/>

			<ApplicantProfileTeamNotes
				v-if="showComments"
				:key="teamNotesRenderKey"
				:application="application"
				@saved="handleTeamNoteSaved"
			/>

			<ApplicantProfileTabs
				:tabs="profileTabs"
				:active-tab="activeTab"
				@select="switchTab"
			/>

			<div
				v-show="activeTab === 'profile'"
				data-test="primary-profile-panel"
				class="pt-4"
			>
				<PersonalityResultsOverview
					:key="applicantProfileId"
					:job-offer-data="jobOffer"
					:applicant-data="applicantProfile"
					:match-data="match"
					:application-id="application ? application.id : null"
					:export-subject-name="applicantFullName"
					:is-work-style-visible="isWorkStyleOnScreen()"
					wheelMode="comparison"
					hideFooter
					@insight-opened="recordAiVerdictInsightOpened"
					@mode-change="recordOverviewModeChange"
				/>

				<ApplicantProfileRequirements
					v-if="!usesCompanyTabLayout"
					class="mt-4"
					:job-offer="jobOffer"
					:applicant-profile="applicantProfile"
					:match="match"
				/>
			</div>

			<div
				v-if="usesCompanyTabLayout"
				v-show="activeTab === 'requirements'"
				data-test="requirements-panel"
				class="pt-4"
			>
				<ApplicantProfileRequirements
					:job-offer="jobOffer"
					:applicant-profile="applicantProfile"
					:match="match"
					company-layout
				/>
			</div>

			<ApplicantProfileAttachments
				v-show="activeTab === 'cv'"
				v-model="selectedAttachmentId"
				class="pt-4"
				:attachments="availableAttachments"
				:loading="attachmentsLoading"
			/>

			<div
				v-if="activeTab === 'history' && applicationHistoryEnabled"
				class="pt-4"
			>
				<ApplicantApplicationHistory
					:key="application.id"
					:application="application"
					:applicant-name="applicantFullName"
					:applicant-avatar-url="applicantAvatarUrl"
					:refresh-key="applicationHistoryRefreshKey"
				/>
			</div>
		</div>

		<div v-else class="p-4 text-center">
			<Spinner type="primary">{{ $t('general.loading') }}</Spinner>
		</div>

		<InterviewInvitationModal
			v-model="showInterviewInvitationModal"
			:applications="interviewInvitationApplications"
			:submitting="interviewInvitationSubmitting"
			@busy="interviewInvitationSending = $event"
			@close="closeInterviewInvitationModal"
			@submit="submitInterviewInvitation"
		/>
	</Modal>
</template>

<script>
import { beginAvailabilityRequest, isApplicationProfileDeactivated } from '~/utils/applicationAvailability'
import Spinner from '~/components/ui/common/Spinner'
import Modal from '~/components/ui/layout/Modal'
import Button from '~/components/ui/common/Button'
import {
	IconUsers,
	IconAlertTriangle,
	IconRepeat,
	IconArrowLeft,
	IconArrowRight
} from '~/components/Icons'
import InterviewInvitationModal from '~/components/InterviewInvitationModal'
import interviewInvitationFlow from '~/mixins/interviewInvitationFlow'
import { PersonalityResultsOverview } from '~/components/Personality'
import {
	ApplicantApplicationHistory,
	ApplicantProfileHeader,
	ApplicantProfileTeamNotes,
	ApplicantProfileRequirements,
	ApplicantProfileAttachments,
	ApplicantProfileTabs
} from './_internals'
import {
	buildApplicantHeaderIdentity,
	isApplicationHistoryEnabled
} from '~/utils/applicantProfileModal'
import {
	queuePendingFeedback,
	rollbackPendingFeedback
} from '~/utils/feedbackTrigger'
import {
	summarizeOverviewEngagement,
	summarizeTabEngagement
} from '~/utils/modalEngagement'

export default {
	mixins: [interviewInvitationFlow],
	components: {
		ApplicantApplicationHistory,
		ApplicantProfileHeader,
		ApplicantProfileTeamNotes,
		ApplicantProfileRequirements,
		ApplicantProfileAttachments,
		ApplicantProfileTabs,
		Spinner,
		Modal,
		Button,
		IconUsers,
		IconAlertTriangle,
		IconRepeat,
		IconArrowLeft,
		IconArrowRight,
		InterviewInvitationModal,
		PersonalityResultsOverview,
	},
	props: {
		applicantProfileId: {
			type: [String, Number],
			required: true
		},
		jobOfferId: {
			type: [String, Number],
			required: false,
			default: null
		},
		applicantIds: {
			type: Array,
			required: false,
			default: () => []
		},
		matchData: {
			type: Object,
			required: false,
			default: null
		},
		prefetchedApplication: {
			type: Object,
			required: false,
			default: null
		},
		prefetchedApplicantProfile: {
			type: Object,
			required: false,
			default: null
		},
		isTalentPoolContext: {
			type: Boolean,
			required: false,
			default: false
		}
	},
	data() {
		return {
			applicantProfile: null,
			profileRequestId: 0,
			jobOfferRequestId: 0,
			profileRequestsClosed: false,
			jobOffer: null,
			match: null,
			applicationLoading: false,
			loading: true,
			activeTab: 'profile',
			selectedAttachmentId: null,
			attachments: [],
			attachmentsLoading: false,
			showNoCandidatesLeft: false,
			error: false,
			errorMessage: '',
			applicationHistoryRefreshKey: 0,
			// Contradictory decisions made during this review session; surfaced
			// to the parent's Feedback Card on close so they don't interrupt triage.
			pendingFeedbackApplications: [],
			pendingFeedbackUpdates: [],
			closing: false,
			// Per-candidate modal engagement timing. modalOpenedAt and
			// tabEnteredAt are monotonic timestamps; tabSegments accumulates each
			// completed tab dwell. Reset whenever a new candidate is shown.
			// aiVerdict* tracks AI verdict accordion opens for the visit.
			modalOpenedAt: 0,
			tabEnteredAt: 0,
			tabSegments: [],
			aiVerdictOpenedKeys: [],
			aiVerdictOpenedSections: [],
			overviewSegments: [],
			overviewMode: 'comparison',
			overviewModeEnteredAt: 0,
			overviewClockRunning: false,
			overviewVisit: null,
			overviewVisitContext: null
		}
	},
	computed: {
		profileUnavailable() {
			if (!this.$isOrgContext || this.isTalentPoolContext) return false
			const availability =
				this.$store.state.application.availabilityByProfileId?.[this.applicantProfileId]
			if (availability) {
				return isApplicationProfileDeactivated({ applicant_profile: availability })
			}
			const application = this.application || this.prefetchedApplication
			return (
				(this.applicantProfile?.id == this.applicantProfileId &&
					isApplicationProfileDeactivated({ applicant_profile: this.applicantProfile })) ||
				(application?.applicant_profile?.id == this.applicantProfileId &&
					isApplicationProfileDeactivated(application))
			)
		},
		pageTitle() {
			if (this.showNoCandidatesLeft)
				return this.$t('general.no-candidates-left')
			if (!this.applicantProfile) return this.$t('general.loading')
			return (
				this.applicantProfile.user.firstname ||
				this.$t('general.applicant-profile')
			)
		},
		jobOfferTitle() {
			return (
				this.jobOffer?.title ||
				this.jobOffer?.name ||
				this.$t('general.applicant-profile')
			)
		},
		applicantHeaderIdentity() {
			return buildApplicantHeaderIdentity({
				applicantProfile: this.applicantProfile,
				isTalentPoolContext: this.isTalentPoolContext
			})
		},
		applicantFullName() {
			return this.applicantHeaderIdentity.fullName
		},
		applicantAvatarStyle() {
			return {
				backgroundImage: `url(${this.applicantAvatarUrl})`
			}
		},
		applicantAvatarUrl() {
			return (
				this.applicantProfile?.user?.avatar?.url ||
				'/placeholder-avatar.svg'
			)
		},
		applicantPhone() {
			return this.applicantHeaderIdentity.phone
		},
		applicantEmail() {
			return this.applicantHeaderIdentity.email
		},
		applicantProfileLink() {
			return this.applicantHeaderIdentity.profileLink
		},
		application() {
			if (!this.jobOfferId || !this.applicantProfile) return null
			const storedApplication = this.$store.state.application.applications.find(
				v => {
					return (
						v.job_offer_id == this.jobOfferId &&
						v.applicant_profile_id == this.applicantProfile.id
					)
				}
			)

			if (storedApplication) {
				return this.withCurrentProfileAvailability(storedApplication)
			}

			const prefetchedApplicationApplicantId =
				this.prefetchedApplication?.applicant_profile_id ||
				this.prefetchedApplication?.applicant_profile?.id

			if (
				this.prefetchedApplication?.job_offer_id == this.jobOfferId &&
				prefetchedApplicationApplicantId == this.applicantProfile.id
			) {
				return this.withCurrentProfileAvailability(this.prefetchedApplication)
			}

			return null
		},
		currentIndex() {
			if (!this.applicantIds || this.applicantIds.length === 0) return -1
			return this.applicantIds.findIndex(
				id => id == this.applicantProfileId
			)
		},
		hasAttachments() {
			return (
				this.attachmentsEnabled &&
				this.applicantProfile &&
				(this.applicantProfile.has_attachments ||
					(this.attachments && this.attachments.length > 0))
			)
		},
		availableAttachments() {
			if (!this.attachmentsEnabled || !Array.isArray(this.attachments)) {
				return []
			}

			// Normalize malformed payload entries to prevent unstable v-for keys
			return this.attachments.filter(
				attachment =>
					attachment &&
					typeof attachment === 'object' &&
					(attachment.url || attachment.name)
			)
		},
		showComments() {
			return this.$org && this.application && this.jobOfferId
		},
		teamNotesRenderKey() {
			return `${this.application?.id || 'none'}-${this.applicantProfile?.id || 'none'}`
		},
		applicationHistoryEnabled() {
			return isApplicationHistoryEnabled({
				hasOrganization: Boolean(this.$org),
				hasApplication: Boolean(this.application),
				jobOfferId: this.jobOfferId,
				isTalentPoolContext: this.isTalentPoolContext
			})
		},
		showHeaderActionButtons() {
			return (
				(this.$isOrgContext && this.jobOffer?.active) ||
				(this.$applicantProfile &&
					this.application &&
					this.jobOffer?.active)
			)
		},
		showTopFitBadge() {
			return false
		},
		profileTabs() {
			return [
				{
					id: 'profile',
					label: this.primaryProfileTabLabel,
					testId: 'primary-profile-tab'
				},
				...(this.usesCompanyTabLayout
					? [
							{
								id: 'requirements',
								label: this.$t('general.requirements'),
								testId: 'requirements-tab'
							}
					  ]
					: []),
				...(this.attachmentsEnabled
					? [
							{
								id: 'cv',
								label: this.$t('general.attachments'),
								testId: 'attachments-tab'
							}
					  ]
					: []),
				...(this.applicationHistoryEnabled
					? [
							{
								id: 'history',
								label: this.$t('application-history.tab'),
								testId: 'history-tab'
							}
					  ]
					: [])
			]
		},
		primaryProfileTabLabel() {
			if (this.isTalentPoolContext) {
				return this.$t('applicant-profile-modal.work-style')
			}

			return this.usesCompanyTabLayout
				? this.$t('applicant-profile-modal.work-style')
				: this.$t('general.profile')
		},
		usesCompanyTabLayout() {
			return this.$isOrgContext && !this.isTalentPoolContext
		},
		attachmentsEnabled() {
			if (!this.jobOfferId) {
				return false
			}

			if (!this.application) {
				return false
			}

			const allowedStatuses = [
				'invitation',
				'pending',
				'accepted',
				'interview',
				'hired',
				'denied'
			]
			return allowedStatuses.includes(this.application.status)
		}
	},
	watch: {
		profileUnavailable: {
			immediate: true,
			handler(unavailable) {
				if (unavailable) this.handleClose()
			}
		},
		applicantProfileId: {
			async handler(newId, oldId) {
				if (newId && newId !== oldId) {
					this.flushPersonalityOverviewEngagedIfNeeded()
					this.activeTab = 'profile'
					this.resetTabEngagement()
					this.selectedAttachmentId = null
					this.attachments = []
					this.attachmentsLoading = false
					this.showNoCandidatesLeft = false
					await this.loadApplicantData()
				}
			},
			immediate: false
		},
		showInterviewInvitationModal: {
			sync: true,
			handler(isOpen) {
				if (isOpen) {
					this.pauseOverviewClock()
					return
				}

				this.startOverviewClockIfNeeded()
			}
		},
		availableAttachments: {
			handler(newAttachments) {
				if (
					newAttachments &&
					newAttachments.length > 0 &&
					this.selectedAttachmentId == null
				) {
					this.selectedAttachmentId = 0
				}
			},
			immediate: true
		},
		activeTab: {
			async handler(newTab) {
				this.scrollModalToTop()

				if (
					newTab === 'cv' &&
					this.hasAttachments &&
					this.attachments.length === 0 &&
					!this.attachmentsLoading
				) {
					await this.loadAttachments()
					this.scrollModalToTop()
				}
			},
			immediate: false
		}
	},
	methods: {
		beginProfileRequest() {
			return {
				id: ++this.profileRequestId,
				applicantProfileId: this.applicantProfileId,
				sessionGeneration: this.$store.state.application.sessionGeneration,
				availabilityRequest: beginAvailabilityRequest(
					this.$store.state.application,
					(name, payload) => this.$store.commit(`application/${name}`, payload)
				)
			}
		},
		isCurrentProfileRequest(request) {
			return !this.profileRequestsClosed && !this.profileUnavailable &&
				request.id === this.profileRequestId &&
				request.applicantProfileId == this.applicantProfileId &&
				request.sessionGeneration === this.$store.state.application.sessionGeneration
		},
		isCurrentJobOfferRequest(request) {
			return !this.profileRequestsClosed &&
				request.id === this.jobOfferRequestId &&
				request.jobOfferId == this.jobOfferId &&
				request.sessionGeneration === this.$store.state.application.sessionGeneration
		},
		setApplicantProfile(profile, request) {
			if (!this.isCurrentProfileRequest(request)) return false
			// A fresh dossier fetch also reconciles an earlier availability conflict.
			if (this.$isOrgContext && typeof profile?.is_deactivated === 'boolean') {
				this.$store.commit('application/reconcileProfileAvailability', {
					data: { applicant_profile: profile },
					availabilityRequest: request.availabilityRequest
				})
				this.applicantProfile = {
					...profile,
					...this.$store.state.application.availabilityByProfileId?.[profile.id]
				}
			} else {
				this.applicantProfile = profile
			}
			return true
		},
		withCurrentProfileAvailability(application) {
			const availability = this.$store.state.application.availabilityByProfileId?.[this.applicantProfile.id]
			return availability ? {
				...application,
				applicant_profile: { ...application.applicant_profile, ...availability }
			} : application
		},
		getModalScrollContainer() {
			const root = this.$el

			if (!root) return null

			return (
				root.closest?.('.overflow-auto') ||
				root.querySelector?.('.overflow-auto') ||
				null
			)
		},
		scrollModalToTop() {
			this.$nextTick(() => {
				const container = this.getModalScrollContainer()

				if (!container) return

				const scrollToTop = () => {
					if (typeof container.scrollTo === 'function') {
						container.scrollTo({ top: 0, left: 0, behavior: 'auto' })
						return
					}

					container.scrollTop = 0
				}

				if (process.client && window.requestAnimationFrame) {
					window.requestAnimationFrame(scrollToTop)
					return
				}

				scrollToTop()
			})
		},
		handleTeamNoteSaved() {
			this.refreshApplicationHistory()
			this.scrollModalToTop()
		},
		refreshApplicationHistory() {
			this.applicationHistoryRefreshKey += 1
		},
		async loadInitialData() {
			if (this.profileRequestsClosed) return
			const request = this.beginProfileRequest()
			const jobRequest = {
				id: ++this.jobOfferRequestId,
				jobOfferId: this.jobOfferId,
				sessionGeneration: this.$store.state.application.sessionGeneration
			}
			const usesPrefetchedProfile = this.isTalentPoolContext && this.prefetchedApplicantProfile
			try {
				this.loading = true
				this.error = false
				this.errorMessage = ''

				if (usesPrefetchedProfile) {
					this.applicantProfile = this.prefetchedApplicantProfile

					if (this.matchData) {
						this.match = this.matchData
					}

					this.beginOverviewVisitIfReady()

					if (this.jobOfferId) {
						const jobOffer = await this.$axios.$get(
							`/job-offer/${this.jobOfferId}`
						)
						if (!this.isCurrentJobOfferRequest(jobRequest)) return
						this.jobOffer = jobOffer
					}

					return
				}

				const promises = [
					this.$axios.$get(
						`/applicant-profile/${this.applicantProfileId}`
					)
				]

				if (this.jobOfferId) {
					promises.push(
						this.$axios.$get(`/job-offer/${this.jobOfferId}`)
					)

					const [applicantProfile, jobOffer] = await Promise.all(
						promises
					)
					if (this.isCurrentJobOfferRequest(jobRequest)) this.jobOffer = jobOffer
					if (!this.setApplicantProfile(applicantProfile, request)) return

					// Use provided match data for consistency with table view
					if (this.matchData) {
						this.match = this.matchData
					}
				} else {
					const [applicantProfile] = await Promise.all(promises)
					if (!this.setApplicantProfile(applicantProfile, request)) return

					// Use provided match data for consistency with table view
					if (this.matchData) {
						this.match = this.matchData
					}
				}
			} catch (error) {
				const currentRequest = usesPrefetchedProfile
					? this.isCurrentJobOfferRequest(jobRequest)
					: this.isCurrentProfileRequest(request)
				if (!currentRequest) return
				console.error('Error loading initial data:', error)

				if (error.response?.status === 404) {
					this.setError(this.$t('general.applicant-not-found'))
				} else if (error.response?.status === 403) {
					this.setError(this.$t('general.access-denied'))
				} else if (!navigator.onLine) {
					this.setError(this.$t('general.network-error'))
				} else {
					this.setError(this.$t('general.unexpected-error'))
				}
			} finally {
				if (this.isCurrentProfileRequest(request)) {
					this.loading = false
					this.beginOverviewVisitIfReady()
				}
			}
		},
		async loadApplicantData() {
			if (this.profileRequestsClosed) return
			const request = this.beginProfileRequest()
			try {
				this.loading = true
				this.error = false
				this.errorMessage = ''

				if (this.isTalentPoolContext && this.prefetchedApplicantProfile) {
					this.applicantProfile = this.prefetchedApplicantProfile

					if (this.matchData) {
						this.match = this.matchData
					}

					return
				}

				const applicantProfile = await this.$axios.$get(
					`/applicant-profile/${this.applicantProfileId}`
				)
				if (!this.setApplicantProfile(applicantProfile, request)) return

				if (this.matchData) {
					this.match = this.matchData
				}
			} catch (error) {
				if (!this.isCurrentProfileRequest(request)) return
				console.error('Error loading applicant data:', error)

				if (error.response?.status === 404) {
					this.setError(this.$t('general.applicant-not-found'))
				} else if (error.response?.status === 403) {
					this.setError(this.$t('general.access-denied'))
				} else if (!navigator.onLine) {
					this.setError(this.$t('general.network-error'))
				} else {
					this.setError(this.$t('general.unexpected-error'))
				}
			} finally {
				if (this.isCurrentProfileRequest(request)) {
					this.loading = false
					this.beginOverviewVisitIfReady()
				}
			}
		},
		async invite(applicantProfileId, jobOfferId) {
			this.applicationLoading = true
			try {
				await this.$store.dispatch('application/store', {
					jobOfferId: jobOfferId,
					applicantProfileId: applicantProfileId,
					submissionContext: 'organization'
				})
			} finally {
				this.applicationLoading = false
			}
		},
		nowMs() {
			if (typeof performance !== 'undefined' && performance.now) {
				return performance.now()
			}
			return Date.now()
		},
		isWorkStyleOnScreen() {
			return Boolean(
				this.applicantProfile &&
					this.applicantProfile.id == this.applicantProfileId &&
					this.activeTab === 'profile' &&
					!this.error &&
					!this.showNoCandidatesLeft &&
					!this.showInterviewInvitationModal
			)
		},
		pauseOverviewClock(now = this.nowMs()) {
			if (!this.overviewClockRunning) {
				return
			}

			this.closeOverviewSegment(now)
			this.overviewClockRunning = false
		},
		startOverviewClockIfNeeded() {
			if (this.overviewClockRunning || !this.isWorkStyleOnScreen()) {
				return
			}

			this.overviewModeEnteredAt = this.nowMs()
			this.overviewClockRunning = true
		},
		beginOverviewVisitIfReady() {
			if (this.applicantProfile?.id != this.applicantProfileId) {
				return
			}

			this.captureOverviewVisitContext()
			this.startOverviewClockIfNeeded()
		},
		resetTabEngagement() {
			const now = this.nowMs()
			this.modalOpenedAt = now
			this.tabEnteredAt = now
			this.tabSegments = []
			this.aiVerdictOpenedKeys = []
			this.aiVerdictOpenedSections = []
			this.overviewSegments = []
			this.overviewMode = 'comparison'
			this.overviewModeEnteredAt = now
			this.overviewClockRunning = this.isWorkStyleOnScreen()
			this.overviewVisit = {
				pendingDecisions: 0,
				decisionCaptured: false,
				engagedFlushed: false,
				ended: false,
				event: null
			}
			this.overviewVisitContext = null
		},
		buildOverviewVisitContext() {
			const application = this.application
			const appStatus = application?.status

			return {
				jobOfferId: application?.job_offer_id ?? this.jobOfferId,
				applicationId: application?.id ?? null,
				applicantProfileId:
					application?.applicant_profile_id ??
					this.applicantProfile?.id ??
					null,
				stage: appStatus || null,
				isTopFit:
					appStatus === 'denied'
						? Boolean(application?.was_top_fit)
						: Boolean(application?.top_fit),
				match: this.match || null
			}
		},
		captureOverviewVisitContext() {
			this.overviewVisitContext = this.buildOverviewVisitContext()
		},
		closeOverviewSegment(now) {
			if (!this.overviewClockRunning) {
				return
			}

			this.overviewSegments.push({
				mode: this.overviewMode,
				durationMs: now - this.overviewModeEnteredAt
			})
		},
		snapshotOverviewSegments(now = this.nowMs()) {
			const segments = [...this.overviewSegments]
			if (this.overviewClockRunning) {
				segments.push({
					mode: this.overviewMode,
					durationMs: now - this.overviewModeEnteredAt
				})
			}
			return segments
		},
		recordOverviewModeChange(mode) {
			if (!mode || mode === this.overviewMode || !this.overviewClockRunning) {
				return
			}

			const now = this.nowMs()
			this.closeOverviewSegment(now)
			this.overviewModeEnteredAt = now
			this.overviewMode = mode
		},
		flushPersonalityOverviewEngagedIfNeeded() {
			const visit = this.overviewVisit
			if (!visit || visit.ended) {
				return
			}

			visit.ended = true
			if (this.overviewClockRunning || this.overviewSegments.length) {
				visit.event = this.buildPersonalityOverviewEngagedEvent()
			}
			this.pauseOverviewClock()
			this.emitPersonalityOverviewEngaged(visit)
		},
		lockOverviewVisitForDecision(application, status) {
			const decisionEvent = this.buildModalDecisionEvent(application, status)
			const overviewVisit = this.overviewVisit
			overviewVisit.pendingDecisions += 1

			return { decisionEvent, overviewVisit }
		},
		settleOverviewDecision(visit, succeeded) {
			// Keep the captured visit even after navigation replaces the active one.
			visit.pendingDecisions -= 1
			visit.decisionCaptured = visit.decisionCaptured || succeeded
			this.emitPersonalityOverviewEngaged(visit)
		},
		emitPersonalityOverviewEngaged(visit) {
			if (
				!visit.ended ||
				visit.pendingDecisions ||
				visit.decisionCaptured ||
				visit.engagedFlushed
			) {
				return
			}

			visit.engagedFlushed = true
			const event = visit.event
			if (!event) {
				return
			}

			try {
				if (
					typeof this.$analytics?.trackPersonalityOverviewEngaged ===
					'function'
				) {
					this.$analytics.trackPersonalityOverviewEngaged(event)
				}
			} catch (_error) {
				// Analytics must not block close or next/prev.
			}
		},
		buildPersonalityOverviewEngagedEvent() {
			if (!this.$org || !this.$analytics) {
				return null
			}

			const user = this.$store.state.user?.user
			const organizationId = user?.current_organization?.id
			if (!organizationId) {
				return null
			}

			const visit =
				this.overviewVisitContext || this.buildOverviewVisitContext()

			return {
				organizationId,
				userId: user?.id,
				jobOfferId: visit.jobOfferId,
				applicationId: visit.applicationId,
				applicantProfileId: visit.applicantProfileId,
				stage: visit.stage,
				isTopFit: visit.isTopFit,
				listSortActive: null,
				listPosition: null,
				match: visit.match,
				sourceSurface: this.isTalentPoolContext
					? 'talent_pool_modal'
					: 'applicant_profile_modal',
				origin: 'modal',
				overview: summarizeOverviewEngagement(
					this.snapshotOverviewSegments()
				)
			}
		},
		recordAiVerdictInsightOpened({ section, index } = {}) {
			if (!section || typeof index !== 'number') {
				return
			}
			const key = `${section}:${index}`
			if (!this.aiVerdictOpenedKeys.includes(key)) {
				this.aiVerdictOpenedKeys.push(key)
			}
			if (!this.aiVerdictOpenedSections.includes(section)) {
				this.aiVerdictOpenedSections.push(section)
			}
		},
		summarizeAiVerdictEngagement() {
			return {
				ai_verdict_insight_expanded: this.aiVerdictOpenedKeys.length > 0,
				ai_verdict_sections_opened: [...this.aiVerdictOpenedSections],
				ai_verdict_insights_opened_count: this.aiVerdictOpenedKeys.length
			}
		},
		switchTab(tab) {
			if (tab === this.activeTab) {
				return
			}

			const now = this.nowMs()
			if (this.activeTab === 'profile') {
				this.pauseOverviewClock(now)
			}
			this.tabSegments.push({
				tab: this.activeTab,
				durationMs: now - this.tabEnteredAt
			})
			this.tabEnteredAt = now
			this.activeTab = tab
			if (tab === 'profile') {
				this.overviewModeEnteredAt = now
				this.overviewClockRunning = this.isWorkStyleOnScreen()
			}
		},
		// Snapshots the current candidate/timing state into a decision payload.
		// Built synchronously at decision time (before navigation resets state);
		// emitted on success.
		buildModalDecisionEvent(application, status) {
			if (!this.$org || !this.$analytics) {
				return null
			}

			const user = this.$store.state.user?.user
			const organizationId = user?.current_organization?.id
			if (!organizationId || !application) {
				return null
			}

			const now = this.nowMs()
			const segments = [
				...this.tabSegments,
				{ tab: this.activeTab, durationMs: now - this.tabEnteredAt }
			]
			const appStatus = application?.status
			const isTopFit =
				appStatus === 'denied'
					? Boolean(application?.was_top_fit)
					: Boolean(application?.top_fit)

			return {
				organizationId,
				userId: user?.id,
				jobOfferId: application?.job_offer_id ?? this.jobOfferId,
				applicationId: application?.id,
				applicantProfileId:
					application?.applicant_profile_id ?? this.applicantProfileId,
				stage: appStatus,
				isTopFit,
				listSortActive: null,
				listPosition: null,
				match: this.matchData || this.match,
				// Talent-pool decisions go through the same modal but are proactive
				// sourcing, not ATS screening — tag them so analysis can exclude
				// them from the ATS hypothesis.
				sourceSurface: this.isTalentPoolContext
					? 'talent_pool_modal'
					: 'applicant_profile_modal',
				origin: 'modal',
				decision: this.$analytics.mapStatusToDecision(status),
				isBulkAction: false,
				modal: {
					...summarizeTabEngagement(segments),
					...this.summarizeAiVerdictEngagement(),
					...summarizeOverviewEngagement(
						this.snapshotOverviewSegments(now)
					),
					time_in_modal_ms: Math.round(
						Math.max(0, now - this.modalOpenedAt)
					)
				}
			}
		},
		async updateApplication(id, status, sendRejectionEmail, rejectionResult) {
			const application = this.application || this.applicationFromStore(id)

			if (!rejectionResult && this.$isOrgContext && isApplicationProfileDeactivated(application)) return

			if (this.shouldOpenInterviewInvitation(application || id, status)) {
				this.openInterviewInvitationModal([application])
				return
			}

			this.applicationLoading = true

			// Capture the decision context synchronously, before
			// navigateToNextCandidateImmediately() resets tab timing.
			const { decisionEvent, overviewVisit } =
				this.lockOverviewVisitForDecision(this.application, status)

			// Queue feedback synchronously, before navigation/await. Declining
			// doesn't change the personality score, so we don't need the server
			// round-trip to decide. Deferring past the await raced with the modal
			// close (handleClose read an empty queue), dropping the card or
			// surfacing it one decision behind.
			const queuedFeedback = this.queueFeedbackIfContradictory(id, status)
			const navigation = this.captureCandidateNavigation()

			const updatePromise = this.$store
				.dispatch('application/update', {
					id: id,
					status: status,
					sendRejectionEmail,
					rejectionResult
				})
				.then(() => {
					this.settleOverviewDecision(overviewVisit, true)
					this.navigateToNextCandidateImmediately(navigation)
					this.refreshApplicationHistory()
					if (decisionEvent) {
						this.$analytics.trackCandidateDecisionMade(decisionEvent)
					}
				}, error => {
					this.rollbackQueuedFeedback(queuedFeedback)
					this.settleOverviewDecision(overviewVisit, false)
					throw error
				})

			this.pendingFeedbackUpdates = [
				...this.pendingFeedbackUpdates,
				updatePromise
			]

			try {
				await updatePromise
			} finally {
				this.pendingFeedbackUpdates = this.pendingFeedbackUpdates.filter(
					pendingUpdate => pendingUpdate !== updatePromise
				)
				this.applicationLoading = false
			}
		},
		async submitInterviewInvitation(payload) {
			const application =
				this.interviewInvitationApplications[0] || this.application
			const id = application?.id
			const status = 'interview'

			if (!id) {
				return
			}

			this.applicationLoading = true

			const { decisionEvent, overviewVisit } =
				this.lockOverviewVisitForDecision(application, status)
			const queuedFeedback = this.queueFeedbackIfContradictory(id, status)
			const navigation = this.captureCandidateNavigation()

			this.hideInterviewInvitationModal()

			const updatePromise = this.dispatchInterviewInvitationMove(payload)
				.then(() => {
					this.settleOverviewDecision(overviewVisit, true)
					this.navigateToNextCandidateImmediately(navigation)
					this.refreshApplicationHistory()
					if (decisionEvent) {
						this.$analytics.trackCandidateDecisionMade(decisionEvent)
					}
				}, error => {
					this.rollbackQueuedFeedback(queuedFeedback)
					this.clearInterviewInvitationModal()
					this.settleOverviewDecision(overviewVisit, false)
					throw error
				})

			this.pendingFeedbackUpdates = [
				...this.pendingFeedbackUpdates,
				updatePromise
			]

			try {
				await updatePromise
			} finally {
				this.pendingFeedbackUpdates = this.pendingFeedbackUpdates.filter(
					pendingUpdate => pendingUpdate !== updatePromise
				)
				this.applicationLoading = false
			}
		},
		queueFeedbackIfContradictory(id, status) {
			const updated = this.$store.state.application.applications.find(
				app => app.id == id
			)

			const { pending, queuedFeedback } = queuePendingFeedback(
				this.pendingFeedbackApplications,
				updated,
				status
			)

			this.pendingFeedbackApplications = pending

			return queuedFeedback
		},
		rollbackQueuedFeedback(queuedFeedback) {
			this.pendingFeedbackApplications = rollbackPendingFeedback(
				this.pendingFeedbackApplications,
				queuedFeedback
			)
		},
		async settlePendingFeedbackUpdates() {
			while (this.pendingFeedbackUpdates.length) {
				const pendingUpdates = [...this.pendingFeedbackUpdates]

				await Promise.all(
					pendingUpdates.map(pendingUpdate =>
						pendingUpdate.catch(() => null)
					)
				)
			}
		},
		async handleClose() {
			if (this.closing || this.interviewInvitationSending) {
				return
			}

			this.closing = true
			this.flushPersonalityOverviewEngagedIfNeeded()
			this.profileRequestsClosed = true
			await this.settlePendingFeedbackUpdates()
			const pending = this.pendingFeedbackApplications
			this.pendingFeedbackApplications = []
			if (pending.length) {
				// Mixed sessions (some high-denied, some low-hired) are fine:
				// the Feedback Card submits one request per trigger_type.
				this.$emit('feedback', pending)
			}
			this.$emit('close')
			this.closing = false
		},
		captureCandidateNavigation() {
			return {
				applicantIds: [...this.applicantIds],
				currentIndex: this.currentIndex,
				applicantProfileId: this.applicantProfileId
			}
		},
		navigateToNextCandidateImmediately(navigation = this.captureCandidateNavigation()) {
			// The successful update can remove the candidate from the filtered list.
			// Use the click-time order, and respect any manual navigation while waiting.
			if (this.applicantProfileId != navigation.applicantProfileId) return
			const { applicantIds, currentIndex } = navigation
			const eligibleIds = new Set(this.applicantIds.map(String))
			const remainingIds = [
				...applicantIds.slice(currentIndex + 1),
				...applicantIds.slice(0, Math.max(0, currentIndex)).reverse()
			]
			const nextId = remainingIds.find(id =>
				id != this.applicantProfileId && eligibleIds.has(String(id))
			)
			if (nextId != null) {
				this.$emit('navigate', nextId)
			} else {
				this.showNoCandidatesLeft = true
			}
		},
		navigatePrevious() {
			if (this.currentIndex > 0) {
				const previousId = this.applicantIds[this.currentIndex - 1]
				this.$emit('navigate', previousId)
			}
		},
		navigateNext() {
			if (this.currentIndex < this.applicantIds.length - 1) {
				const nextId = this.applicantIds[this.currentIndex + 1]
				this.$emit('navigate', nextId)
			}
		},
		openApplicationInNewTab(applicationId) {
			const url = `/application/${applicationId}?job-offer=${this.jobOfferId}`
			window.open(url, '_blank')
		},
		isTopFit(application) {
			if (!application) {
				return false
			}

			const status = application.status

			if (status === 'denied') {
				return !!application.was_top_fit
			}

			// Invitation is excluded from Top Fit ranking.
			if (status === 'invitation') {
				return false
			}

			if (
				status === 'pending' ||
				status === 'accepted' ||
				status === 'interview' ||
				status === 'hired'
			) {
				return !!application.top_fit
			}

			return false
		},
		setError(message) {
			this.pauseOverviewClock()
			// The error screen destroys the overview; retry mounts Comparison.
			this.overviewMode = 'comparison'
			this.error = true
			this.errorMessage = message
			this.loading = false
		},
		retryLoad() {
			this.error = false
			this.errorMessage = ''
			this.loading = true
			this.loadInitialData()
		},
		async loadAttachments() {
			if (!this.applicantProfile || this.attachmentsLoading) return

			this.attachmentsLoading = true
			try {
				const response = await this.$axios.$get(
					`/applicant-profile/${this.applicantProfile.id}/attachments`
				)
				this.attachments = response.attachments || []
			} catch (error) {
				console.error('Error loading attachments:', error)
				this.attachments = []
			} finally {
				this.attachmentsLoading = false
			}
		}
	},
	beforeDestroy() {
		this.profileRequestsClosed = true
	},
	async mounted() {
		this.resetTabEngagement()
		await this.loadInitialData()
	}
}
</script>
